import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import PhoneAutocomplete from "../../../components/ui/PhoneAutocomplete";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { suggestClients, checkClientPhone } from "../../../lib/clients";

const ClientForm = ({
  client = null,
  onSubmit,
  onCancel,
  isLoading = false,
  serviceOptions = [], // ✅ din părinte (din DB)
  onEditExistingClient,
}) => {
  const { t } = useTranslation();
  // ✅ normalizează opțiunile ca să meargă și dacă vin ca {id,name} sau {value,label}
  const normalizedServices = useMemo(() => {
    return (serviceOptions || [])
      .map((s) => {
        const id = s?.id ?? s?.value;
        const name = s?.name ?? s?.label ?? "";
        if (id === null || id === undefined) return null;
        return { id: Number(id), name: String(name || "").trim() || `Serviciu #${id}` };
      })
      .filter(Boolean);
  }, [serviceOptions]);

  const [formData, setFormData] = useState({
    name: client?.name || "",
    email: client?.email || "",
    phone: client?.phone || "",
    dateOfBirth: client?.dateOfBirth || "",
    gender: client?.gender || "",
    address: client?.address || "",
    notes: client?.notes || "",
    status: client?.status || "active",

    // discount % doar pentru VIP (backend: vip_discount_percent)
    vipDiscountPercent:
      client?.vipDiscountPercent === null || client?.vipDiscountPercent === undefined
        ? ""
        : String(client.vipDiscountPercent),

    // ✅ aici ținem ID-uri de Service (nu stringuri hardcodate)
    preferredServices: (client?.preferred_service_ids || client?.preferredServices || [])
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x)),

    preferences: {
      email: client?.preferences?.email ?? true,
      sms: client?.preferences?.sms ?? true,
      phone: client?.preferences?.phone ?? false,
    },
  });


  const [submitError, setSubmitError] = useState("");
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [nameSuggestOpen, setNameSuggestOpen] = useState(false);
  const [nameSuggestLoading, setNameSuggestLoading] = useState(false);
  const [pickedExistingByName, setPickedExistingByName] = useState(null);

  const [phoneCheck, setPhoneCheck] = useState({ phone_normalized: "", matches: [] });
  const [phoneReassignFromId, setPhoneReassignFromId] = useState(null);

  const nameDebounceRef = useRef(null);
  const phoneDebounceRef = useRef(null);
  const nameSuggestWrapRef = useRef(null);

  const isCreate = !client;

  // --- Name suggestions (only on create) ---
  useEffect(() => {
    if (!isCreate) return;

    const q = String(formData?.name || "").trim();
    if (!q || q.length < 2) {
      setNameSuggestions([]);
      return;
    }

    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(async () => {
      try {
        setNameSuggestLoading(true);
        const rows = await suggestClients(q, { limit: 8 });
        setNameSuggestions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setNameSuggestions([]);
      } finally {
        setNameSuggestLoading(false);
      }
    }, 250);

    return () => {
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, formData?.name]);

  // --- Phone check (create + edit) ---
  useEffect(() => {
    const p = String(formData?.phone || "").trim();
    if (!p || p.length < 3) {
      setPhoneCheck({ phone_normalized: "", matches: [] });
      setPhoneReassignFromId(null);
      return;
    }

    if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
    phoneDebounceRef.current = setTimeout(async () => {
      try {
        const res = await checkClientPhone(p, { excludeId: client?.id });
        setPhoneCheck(res || { phone_normalized: "", matches: [] });

        // if current reassign target is no longer valid -> clear
        const ids = new Set((res?.matches || []).map((m) => m?.id));
        if (phoneReassignFromId && !ids.has(phoneReassignFromId)) {
          setPhoneReassignFromId(null);
        }
      } catch (e) {
        setPhoneCheck({ phone_normalized: "", matches: [] });
      }
    }, 250);

    return () => {
      if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.phone, client?.id]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!nameSuggestOpen) return;

    const onPointerDown = (e) => {
      const el = nameSuggestWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) {
        setNameSuggestOpen(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setNameSuggestOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [nameSuggestOpen]);

  const genderOptions = [
    { value: "", label: t("clients.genderSelect") },
    { value: "male", label: t("clients.genderMale") },
    { value: "female", label: t("clients.genderFemale") },
    { value: "other", label: t("clients.genderOther") },
  ];

  const statusOptions = [
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
    { value: "vip", label: "VIP" },
  ];

  const handleInputChange = (field, value) => {
    if (submitError) setSubmitError("");
    if (field === "name") {
      setPickedExistingByName(null);
      setNameSuggestOpen(true);
    }
    if (field === "phone") {
      setPhoneReassignFromId(null);
    }

    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "status" && value !== "vip") next.vipDiscountPercent = "";
      return next;
    });

    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePreferenceChange = (preference, checked) => {
    setFormData((prev) => ({
      ...prev,
      preferences: { ...prev?.preferences, [preference]: checked },
    }));
  };

  const handleServiceToggle = (serviceId) => {
    const id = Number(serviceId);
    if (!Number.isFinite(id)) return;

    setFormData((prev) => {
      const current = Array.isArray(prev?.preferredServices) ? prev.preferredServices : [];
      const set = new Set(current.map((x) => Number(x)).filter(Number.isFinite));

      if (set.has(id)) set.delete(id);
      else set.add(id);

      return { ...prev, preferredServices: Array.from(set) };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = "Numele este obligatoriu";

    const email = (formData?.email || "").trim();
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email-ul nu este valid";
    }

    if (!formData?.phone?.trim()) newErrors.phone = "Numărul de telefon este obligatoriu";
    else if (!/^[0-9+\-\s()]+$/?.test(formData?.phone)) newErrors.phone = "Numărul de telefon nu este valid";

    if (formData?.status === "vip") {
      const raw = String(formData?.vipDiscountPercent ?? "").trim();
      if (raw !== "") {
        const num = Number(raw);
        if (Number.isNaN(num)) newErrors.vipDiscountPercent = "Discount-ul trebuie să fie un număr";
        else if (num < 0 || num > 100) newErrors.vipDiscountPercent = "Discount-ul trebuie să fie între 0 și 100";
      }
    }


    // dacă e creare și telefonul există deja, trebuie ales: edit existent / mută numărul / schimbă numărul
    const hasConflict = Array.isArray(phoneCheck?.matches) && phoneCheck.matches.length > 0;
    if (!client && hasConflict && !phoneReassignFromId) {
      newErrors.phone =
        newErrors.phone ||
        "Numărul este deja folosit. Alege: Editează client existent, Mută numărul la client nou, sau schimbă numărul.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError("");

    if (!validateForm()) return;

    const payload = {
      ...formData,
      email: (formData.email || "").trim(),
      phone: (formData.phone || "").trim(),
      ...(phoneReassignFromId
        ? { forcePhoneReassign: true, fromClientId: phoneReassignFromId }
        : {}),
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      const code = error?.response?.data?.code;
      const detail = error?.response?.data?.detail;

      if (code === "phone_conflict") {
        const matches = error?.response?.data?.matches || [];
        setPhoneCheck({ phone_normalized: error?.response?.data?.phone || "", matches });
        setSubmitError(detail || "Telefonul este deja folosit de alt client.");
        return;
      }

      setSubmitError(detail || error?.message || "A apărut o eroare la salvare.");
    }
  };


  return (
    <div className="bg-background border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-card-foreground">
          {client ? t("clients.formTitleEdit") : t("clients.formTitleNew")}
        </h2>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <Icon name="X" size={20} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {submitError ? (
          <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {submitError}
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <div className="relative" ref={nameSuggestWrapRef}>
            <Input
              label={t("clients.fullNameLabel")}
              placeholder={t("clients.fullNamePlaceholder")}
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={errors.name}
              required
              onFocus={() => setNameSuggestOpen(true)}
            />

            {/* Autocomplete (doar la creare) */}
            {isCreate && nameSuggestOpen && (nameSuggestLoading || (nameSuggestions && nameSuggestions.length > 0)) ? (
              <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg max-h-60 overflow-auto">
                {nameSuggestLoading ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Caut...</div>
                ) : (
                  nameSuggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-muted"
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        setPickedExistingByName(s);
                        setNameSuggestOpen(false);
                      }}
                    >
                      <div className="text-sm font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {(s.phone || "—")}{s.email ? ` • ${s.email}` : ""}
                      </div>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          {isCreate && pickedExistingByName ? (
            <div className="rounded border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              Există deja un client cu nume similar: <b>{pickedExistingByName.name}</b>
              {pickedExistingByName.phone ? ` (${pickedExistingByName.phone})` : ""}.
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onEditExistingClient?.(pickedExistingByName.id)}
                >
                  Editează clientul existent
                </Button>
                <Button type="button" variant="ghost" onClick={() => setPickedExistingByName(null)}>
                  Continuă cu client nou
                </Button>
              </div>
            </div>
          ) : null}


          <Input
            label={t("common.email")}
            type="email"
            placeholder={t("clients.emailPlaceholder")}
            value={formData?.email}
            onChange={(e) => handleInputChange("email", e?.target?.value)}
            error={errors?.email}
          />


          <div className="space-y-2">
            <PhoneAutocomplete
              label={t("common.phone")}
              placeholder="07xx xxx xxx"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              onPickClient={(c) => onEditExistingClient?.(c.id)}
              error={errors.phone}
              required
            />

            {Array.isArray(phoneCheck?.matches) && phoneCheck.matches.length > 0 ? (
              <div className="rounded border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                <div className="font-medium">
                  {phoneCheck.matches.length === 1 ? (
                    <>
                      Numărul {phoneCheck?.phone_normalized || formData.phone} e alocat lui <b>{phoneCheck.matches[0]?.name}</b>.
                      {" "}Vrei să-l folosești pe <b>{phoneCheck.matches[0]?.name}</b> sau să-l muți la client nou?
                    </>
                  ) : (
                    <>
                      Numărul {phoneCheck?.phone_normalized || formData.phone} e alocat unor clienți.
                      {" "}Vrei să folosești un client existent sau să-l muți la client nou?
                    </>
                  )}
                </div>

                <div className="mt-2 space-y-2">
                  {phoneCheck.matches.map((m) => (
                    <div key={m.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <b>{m.name}</b>
                        <div className="text-xs text-muted-foreground">
                          {(m.phone || "—")}{m.email ? ` • ${m.email}` : ""}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onEditExistingClient?.(m.id)}
                        >
                          Editează
                        </Button>

                        {isCreate ? (
                          <Button
                            type="button"
                            variant={phoneReassignFromId === m.id ? "default" : "outline"}
                            onClick={() => {
                              const ok = window.confirm(
                                `Numărul e alocat lui ${m.name}.\n\nVrei să-l muți la client nou?\n\n(La salvare: vechiului client i se șterge numărul, iar clientul nou îl primește.)`
                              );
                              if (!ok) return;
                              setPhoneReassignFromId(m.id);
                            }}
                          >
                            Mută numărul la client nou
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>

                {isCreate ? (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Dacă alegi "Mută numărul", numărul va fi șters de la clientul vechi și atribuit acestui client nou
                    la salvare.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>


          <Input
            label={t("clients.dobLabel")}
            type="date"
            value={formData?.dateOfBirth}
            onChange={(e) => handleInputChange("dateOfBirth", e?.target?.value)}
          />

          <Select label={t("clients.genderLabel")} options={genderOptions} value={formData?.gender} onChange={(v) => handleInputChange("gender", v)} />

          <Select label={t("clients.statusLabel")} options={statusOptions} value={formData?.status} onChange={(v) => handleInputChange("status", v)} required />
        </div>

        {formData?.status === "vip" && (
          <div className="bg-muted/40 border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Crown" size={16} className="text-accent" />
              <h3 className="text-sm font-semibold text-foreground">Setări VIP</h3>
            </div>
            <Input
              label="Discount VIP (%)"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              step={0.01}
              placeholder="ex: 10"
              value={formData?.vipDiscountPercent}
              onChange={(e) => handleInputChange("vipDiscountPercent", e?.target?.value)}
              error={errors?.vipDiscountPercent}
              description="Lasă gol dacă VIP nu are discount special. Se aplică automat la programări."
            />
          </div>
        )}

        <Input
          label={t("clients.addressLabel")}
          type="text"
          placeholder={t("clients.addressPlaceholder")}
          value={formData?.address}
          onChange={(e) => handleInputChange("address", e?.target?.value)}
        />

        {/* ✅ Preferred Services din DB */}
        {/* <div>
          <label className="block text-sm font-medium text-foreground mb-3">Servicii Preferate</label>

          {normalizedServices.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Nu există servicii active pentru acest business (sau nu au fost încărcate).
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {normalizedServices.map((service) => (
                <Checkbox
                  key={service.id}
                  label={service.name}
                  checked={formData?.preferredServices?.includes(service.id)}
                  onChange={() => handleServiceToggle(service.id)}
                />
              ))}
            </div>
          )}
        </div> */}

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">{t("clients.commPrefs")}</label>
          <div className="space-y-3">
            <Checkbox
              label={t("clients.prefEmail")}
              description={t("clients.prefEmailDesc")}
              checked={formData?.preferences?.email}
              onChange={(e) => handlePreferenceChange("email", e?.target?.checked)}
            />
            <Checkbox
              label={t("clients.prefSms")}
              description={t("clients.prefSmsDesc")}
              checked={formData?.preferences?.sms}
              onChange={(e) => handlePreferenceChange("sms", e?.target?.checked)}
            />
            <Checkbox
              label={t("clients.prefPhone")}
              description={t("clients.prefPhoneDesc")}
              checked={formData?.preferences?.phone}
              onChange={(e) => handlePreferenceChange("phone", e?.target?.checked)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t("clients.notesLabel")}</label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            rows={4}
            placeholder={t("clients.notesFormPlaceholder")}
            value={formData?.notes}
            onChange={(e) => handleInputChange("notes", e?.target?.value)}
          />
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="default" loading={isLoading} iconName="Save" iconPosition="left">
            {client ? t("clients.updateBtn") : t("clients.createBtn")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
